/*
 * Sneakerwear cart drawer.
 * "You may also like" carousel (snippets/sw-cart-upsell.liquid):
 * - Arrows scroll the carousel
 * - "+ Add" puts the chosen size in the cart, then tells the theme the cart changed
 *   (same CartLinesUpdateEvent the product page uses), so the drawer, the rewards bar
 *   and the header cart count all refresh on their own.
 * First-order code (snippets/sw-cart-rewards.liquid):
 * - "Apply" adds the code to the cart the same way the theme's discount field does
 *   (assets/cart-discount.js), then re-renders the drawer. Without JavaScript the
 *   button is a plain link to /discount/CODE, which applies the code too.
 */
import { CartLinesUpdateEvent, CartErrorEvent, CartDiscountUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

/* `Theme` is the global const declared in snippets/scripts.liquid. It is not a property of
   `window`, so it must be read as `Theme`, never `window.Theme`. */
/* global Theme */

const SIZE_FIRST_MESSAGE = 'Pick your size first';
const GENERIC_ERROR = 'Could not add this item. Please try again.';
const CODE_ERROR = 'The code couldn’t be applied. Please try again at checkout.';
const DRAWER_SECTION_ID = 'cart-drawer-section';

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Upsell copy in the storefront language, set on the carousel by snippets/sw-cart-upsell.liquid.
 * @param {Element} card @param {'swMsgSize' | 'swMsgError'} key @param {string} fallback
 */
function upsellMessage(card, key, fallback) {
  const upsell = card.closest('.sw-upsell');
  return (upsell instanceof HTMLElement && upsell.dataset[key]) || fallback;
}

/** @param {Element} card @param {string} message */
function showError(card, message) {
  const error = card.querySelector('[data-sw-upsell-error]');
  if (error) error.textContent = message;
  card.classList.toggle('is-invalid', Boolean(message));
}

/** @param {HTMLElement} arrow */
function scrollCarousel(arrow) {
  const track = arrow.closest('.sw-upsell')?.querySelector('[data-sw-upsell-track]');
  if (!(track instanceof HTMLElement)) return;
  const card = track.querySelector('.sw-upsell__card');
  const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
  const step = card instanceof HTMLElement ? card.offsetWidth + gap : track.clientWidth * 0.8;
  const direction = Number(arrow.dataset.swUpsellDir) || 1;
  const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
  track.scrollBy({ left: step * direction * rtl, behavior: reducedMotion() ? 'auto' : 'smooth' });
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

/** @param {HTMLButtonElement} button */
async function addToCart(button) {
  const card = button.closest('.sw-upsell__card');
  if (!card || button.getAttribute('aria-busy') === 'true') return;

  const field = card.querySelector('[data-sw-upsell-variant]');
  const variantId = field instanceof HTMLInputElement || field instanceof HTMLSelectElement ? field.value : '';
  if (!variantId) {
    showError(card, upsellMessage(card, 'swMsgSize', SIZE_FIRST_MESSAGE));
    if (field instanceof HTMLSelectElement) field.focus();
    return;
  }
  showError(card, '');

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);

  button.setAttribute('aria-busy', 'true');
  button.disabled = true;

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const result = await response.json();
    const cart = await fetchCart();
    const failed = Boolean(result.status) || !response.ok;

    if (failed) {
      showError(card, result.description || result.message || upsellMessage(card, 'swMsgError', GENERIC_ERROR));
      button.dispatchEvent(
        new CartErrorEvent({ error: result.message || 'Add to cart failed', code: 'INVALID' })
      );
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: 1,
        source: 'sw-cart-upsell',
        didError: failed,
        ...(failed ? {} : { sections: result.sections }),
      },
    });
  } catch (error) {
    deferred.reject(error);
    showError(card, upsellMessage(card, 'swMsgError', GENERIC_ERROR));
    button.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Network error during add to cart',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
  } finally {
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }
}

/** Codes already on the cart, read from the theme's discount pills (snippets/cart-summary.liquid). */
function cartDiscountCodes() {
  const scope = document.getElementById('cart-drawer') || document;
  /** @type {string[]} */
  const codes = [];
  for (const pill of scope.querySelectorAll('.cart-discount__pill')) {
    if (pill instanceof HTMLElement && pill.dataset.discountCode) codes.push(pill.dataset.discountCode);
  }
  return codes;
}

/** @param {Element} box @param {string} message */
function showCodeError(box, message) {
  const error = box.querySelector('[data-sw-code-error]');
  if (error) error.textContent = message;
}

/** @param {HTMLAnchorElement} link */
async function applyCode(link) {
  const code = (link.dataset.swApplyCode || '').trim();
  const box = link.closest('[data-sw-code]');
  if (!code || !box || link.getAttribute('aria-busy') === 'true') return;

  const sectionHost = link.closest('cart-items-component');
  const sectionId = (sectionHost instanceof HTMLElement && sectionHost.dataset.sectionId) || DRAWER_SECTION_ID;
  const errorMessage = link.dataset.swError || CODE_ERROR;

  const existing = cartDiscountCodes();
  if (existing.some((value) => value.toUpperCase() === code.toUpperCase())) return;
  const allCodes = [...existing, code];

  showCodeError(box, '');
  link.setAttribute('aria-busy', 'true');

  const deferred = CartDiscountUpdateEvent.createPromise();
  link.dispatchEvent(
    new CartDiscountUpdateEvent({
      discountCodes: allCodes.map((value) => ({ code: value })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: allCodes.join(','), sections: [sectionId] }),
    });
    if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
    const data = await response.json();
    deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data) });

    const codes = Array.isArray(data.discount_codes) ? data.discount_codes : [];
    const result = codes.find(
      (/** @type {{ code: string; applicable: boolean }} */ entry) =>
        String(entry.code).toUpperCase() === code.toUpperCase()
    );
    if (!result || result.applicable === false) {
      showCodeError(box, errorMessage);
      link.removeAttribute('aria-busy');
      return;
    }

    const html = data.sections ? data.sections[sectionId] : null;
    if (typeof html !== 'string') {
      link.removeAttribute('aria-busy');
      return;
    }
    await morphSection(sectionId, html, { mode: 'hydration' });
    const done = document.querySelector('#cart-drawer [data-sw-code-done]');
    if (done instanceof HTMLElement) done.focus({ preventScroll: true });
  } catch (error) {
    deferred.reject(error);
    showCodeError(box, errorMessage);
    link.removeAttribute('aria-busy');
    link.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Failed to apply discount',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const apply = target.closest('[data-sw-apply-code]');
  if (apply instanceof HTMLAnchorElement) {
    const plainClick = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    const canFetch = typeof Theme !== 'undefined' && Boolean(Theme.routes?.cart_update_url);
    // Otherwise the link itself applies the code (/discount/CODE).
    if (plainClick && canFetch) {
      event.preventDefault();
      applyCode(apply);
    }
    return;
  }

  const arrow = target.closest('[data-sw-upsell-dir]');
  if (arrow instanceof HTMLElement) {
    scrollCarousel(arrow);
    return;
  }

  const add = target.closest('[data-sw-upsell-add]');
  if (add instanceof HTMLButtonElement) addToCart(add);
});

document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-sw-upsell-variant]')) return;
  const card = select.closest('.sw-upsell__card');
  if (!card) return;
  showError(card, '');
  const price = select.selectedOptions[0]?.dataset.price;
  const priceElement = card.querySelector('[data-sw-upsell-price]');
  if (price && priceElement) priceElement.textContent = price;
});